import { Review, PlatformRating, FacebookRatingPlatform } from '../type';
import {
    getAllReviews,
    updateReviewsStatus,
    getCountUnreadReviewsFromData,
    getReviewsComments,
} from './SocialNetworkService';
import { invalidateUnreadReviewsCache } from '../hooks/useUnreadReviews';
import { updateGlobalUnreadCount } from '../hooks/useGlobalUnreadCount';
import { NetworkLoadingState, NetworkResponse } from '../types/NetworkTypes';

interface CachedData {
    reviews: Review[];
    platformRatings: PlatformRating[];
    lastFetch: number;
    loadingState: NetworkLoadingState;
    hasInitialData: boolean; // Flag to know if we already have data
    isInitializing: boolean; // Flag to prevent multiple requests
}

interface PendingUpdate {
    reviewId: string;
    newStatus: string;
    timestamp: number;
}

class ReviewsCacheService {
    private cache = new Map<string, CachedData>();
    private pendingUpdates = new Map<string, PendingUpdate[]>();
    private syncInProgress = new Set<string>();
    private initializationPromises = new Map<string, Promise<any>>(); // To prevent concurrent requests
    private recentReviewsPromises = new Map<string, Promise<any>>(); // To coordinate with recent reviews
    private readonly CACHE_TTL = 5 * 60 * 1000;
    private subscribers = new Map<string, Set<() => void>>(); // To notify hooks of changes
    private commentsPromises = new Map<string, Promise<any>>(); // To avoid multiple calls to reviews-comments
    private preloadingComments = new Set<string>(); // To track current preloads

    async getReviews(
        userId: string,
        forceRefresh = false,
        onNetworkComplete?: (
            network: keyof NetworkLoadingState,
            data: NetworkResponse | null,
        ) => void,
    ): Promise<{
        reviews: Review[];
        platformRatings: PlatformRating[];
        loadingState: NetworkLoadingState;
    }> {
        const cached = this.cache.get(userId);
        const now = Date.now();

        const initialLoadingState: NetworkLoadingState = {
            gmb: true,
            facebook: true,
            opinionSystem: true,
        };

        // If we have valid cached data and no forceRefresh
        if (
            !forceRefresh &&
            cached &&
            cached.hasInitialData &&
            !Object.values(cached.loadingState).some(state => state) &&
            now - cached.lastFetch < this.CACHE_TTL
        ) {
            return {
                reviews: [...cached.reviews],
                platformRatings: [...cached.platformRatings],
                loadingState: { ...cached.loadingState },
            };
        }

        // If initialization is already in progress, wait for its completion
        if (this.initializationPromises.has(userId)) {
            try {
                await this.initializationPromises.get(userId);
                const updatedCache = this.cache.get(userId);
                if (updatedCache) {
                    return {
                        reviews: [...updatedCache.reviews],
                        platformRatings: [...updatedCache.platformRatings],
                        loadingState: { ...updatedCache.loadingState },
                    };
                }
            } catch (error) {
                console.warn(
                    '[ReviewsCacheService] Previous initialization failed, trying again:',
                    (error as Error).message,
                );
                // Initialization failed, continue with a new attempt
            }
        }

        // If we already have data but a request is in progress, return existing data
        if (cached && cached.hasInitialData && cached.isInitializing) {
            return {
                reviews: [...cached.reviews],
                platformRatings: [...cached.platformRatings],
                loadingState: { ...cached.loadingState },
            };
        }

        // Initialize cache if not done yet or if forceRefresh
        if (!cached || forceRefresh) {
            this.cache.set(userId, {
                reviews: cached?.hasInitialData ? cached.reviews : [],
                platformRatings: cached?.hasInitialData ? cached.platformRatings : [],
                lastFetch: cached?.lastFetch || 0,
                loadingState: initialLoadingState,
                hasInitialData: cached?.hasInitialData || false,
                isInitializing: true,
            });
        }

        const initPromise = this.fetchAndCacheReviews(userId, (network, data) => {
            const currentCache = this.cache.get(userId);
            if (currentCache) {
                const newLoadingState = { ...currentCache.loadingState };
                newLoadingState[network] = false;

                const updatedPlatformRatings = [...currentCache.platformRatings];

                let updatedReviews = [...currentCache.reviews];
                if (data && data.reviews && data.reviews.length > 0) {
                    const networkMapping = {
                        gmb: 'google_my_business',
                        facebook: 'facebook',
                        opinionSystem: 'opinion_system',
                    };
                    const networkName = networkMapping[network as keyof typeof networkMapping];
                    updatedReviews = updatedReviews.filter(
                        review => review.network !== networkName,
                    );
                    updatedReviews = [...updatedReviews, ...data.reviews];
                    updatedReviews.sort(
                        (a, b) =>
                            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
                    );
                }

                if (data) {
                    if (network === 'gmb' && data.averageRating !== undefined) {
                        const index = updatedPlatformRatings.findIndex(
                            p => p.platform === 'google_my_business',
                        );
                        const newRating = {
                            platform: 'google_my_business' as const,
                            rating: data.averageRating || 0,
                            totalReviews: data.totalReviewCount || data.reviews?.length || 0,
                        };
                        if (index >= 0) {
                            updatedPlatformRatings[index] = newRating;
                        } else {
                            updatedPlatformRatings.push(newRating);
                        }
                    } else if (network === 'facebook' && data.averageRating !== undefined) {
                        const index = updatedPlatformRatings.findIndex(
                            p => p.platform === 'facebook',
                        );
                        const newRating: FacebookRatingPlatform = {
                            platform: 'facebook',
                            rating: data.averageRating || 0,
                            totalReviews: data.reviews?.length || 0,
                        };
                        if (index >= 0) {
                            updatedPlatformRatings[index] = newRating;
                        } else {
                            updatedPlatformRatings.push(newRating);
                        }
                    } else if (network === 'opinionSystem' && data.averageRating !== undefined) {
                        const index = updatedPlatformRatings.findIndex(
                            p => p.platform === 'opinion_system',
                        );
                        const newRating = {
                            platform: 'opinion_system' as const,
                            rating: data.averageRating || 0,
                            totalReviews: data.totalReviewCount || data.reviews?.length || 0,
                        };
                        if (index >= 0) {
                            updatedPlatformRatings[index] = newRating;
                        } else {
                            updatedPlatformRatings.push(newRating);
                        }
                    }
                }

                this.cache.set(userId, {
                    ...currentCache,
                    loadingState: newLoadingState,
                    platformRatings: updatedPlatformRatings,
                    reviews: updatedReviews,
                });

                const newUnreadCount = getCountUnreadReviewsFromData(updatedReviews);
                updateGlobalUnreadCount(userId, newUnreadCount);

                if (network === 'facebook' && data && data.reviews && data.reviews.length > 0) {
                    this.preloadFacebookCommentsInBackground(userId);
                }

                this.notifySubscribers(userId);
            }

            onNetworkComplete?.(network, data);
        });
        this.initializationPromises.set(userId, initPromise);

        const currentCache = this.cache.get(userId);
        if (currentCache) {
            initPromise
                .then(() => {
                    this.initializationPromises.delete(userId);
                })
                .catch(error => {
                    this.initializationPromises.delete(userId);
                    console.error(
                        `[ReviewsCacheService] ❌ Erreur lors des requêtes pour ${userId}:`,
                        error,
                    );
                });

            return {
                reviews: [...currentCache.reviews],
                platformRatings: [...currentCache.platformRatings],
                loadingState: { ...currentCache.loadingState },
            };
        }

        try {
            const result = await initPromise;
            this.initializationPromises.delete(userId);
            return result;
        } catch (error) {
            this.initializationPromises.delete(userId);
            console.error(
                '[ReviewsCacheService] Fetch failed for user:',
                userId,
                (error as Error).message,
            );

            // In case of error, restore a consistent state
            const currentCache = this.cache.get(userId);
            if (currentCache) {
                this.cache.set(userId, {
                    ...currentCache,
                    loadingState: {
                        gmb: false,
                        facebook: false,
                        opinionSystem: false,
                    },
                    isInitializing: false,
                });

                // If we already had data, return it instead of throwing
                if (currentCache.hasInitialData) {
                    return {
                        reviews: [...currentCache.reviews],
                        platformRatings: [...currentCache.platformRatings],
                        loadingState: {
                            gmb: false,
                            facebook: false,
                            opinionSystem: false,
                        },
                    };
                }
            }

            throw error;
        }
    }

    private async fetchAndCacheReviews(
        userId: string,
        onNetworkComplete?: (
            network: keyof NetworkLoadingState,
            data: NetworkResponse | null,
        ) => void,
    ): Promise<{
        reviews: Review[];
        platformRatings: PlatformRating[];
        loadingState: NetworkLoadingState;
    }> {
        try {
            if (this.recentReviewsPromises.has(userId)) {
                try {
                    await this.recentReviewsPromises.get(userId);
                    await new Promise(resolve => setTimeout(resolve, 2000));
                } catch (error) {
                    console.warn(
                        `[fetchAndCacheReviews] ⚠️ getRecentReviewsWithComments a échoué pour ${userId}, continuons quand même`,
                    );
                }
            }

            const response = await getAllReviews(userId, false, onNetworkComplete);

            if (!response) {
                const emptyLoadingState = {
                    gmb: false,
                    facebook: false,
                    opinionSystem: false,
                };

                const cached = this.cache.get(userId);
                this.cache.set(userId, {
                    reviews: cached?.hasInitialData ? cached.reviews : [],
                    platformRatings: cached?.hasInitialData ? cached.platformRatings : [],
                    lastFetch: Date.now(),
                    loadingState: emptyLoadingState,
                    hasInitialData: cached?.hasInitialData || false,
                    isInitializing: false,
                });

                return {
                    reviews: cached?.hasInitialData ? cached.reviews : [],
                    platformRatings: cached?.hasInitialData ? cached.platformRatings : [],
                    loadingState: emptyLoadingState,
                };
            }

            // response is of type NetworkReviewsData from your SocialNetworkService
            const reviews = response.reviews || [];
            const platformRatings: PlatformRating[] = [];

            // Using NetworkReviewsData structure
            if (response.sources?.gmb) {
                platformRatings.push({
                    platform: 'google_my_business',
                    rating: response.sources.gmb.averageRating || 0,
                    totalReviews: response.sources.gmb.count || 0,
                });
            }

            if (response.sources?.facebook) {
                const fbRating: FacebookRatingPlatform = {
                    platform: 'facebook',
                    rating: response.sources.facebook.averageRating || 0,
                    totalReviews: response.sources.facebook.count || 0,
                };
                platformRatings.push(fbRating);
            }

            if (response.sources?.opinion_system) {
                platformRatings.push({
                    platform: 'opinion_system',
                    rating: response.sources.opinion_system.averageRating || 0,
                    totalReviews: response.sources.opinion_system.count || 0,
                });
            }

            const sortedReviews = [...reviews].sort(
                (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
            );

            const now = Date.now();
            const finalLoadingState = response.loadingState || {
                gmb: false,
                facebook: false,
                opinionSystem: false,
            };

            this.cache.set(userId, {
                reviews: sortedReviews,
                platformRatings,
                lastFetch: now,
                loadingState: finalLoadingState,
                hasInitialData: true,
                isInitializing: false,
            });

            return {
                reviews: sortedReviews,
                platformRatings,
                loadingState: finalLoadingState,
            };
        } catch (error) {
            console.error('Error loading reviews from cache service:', error);

            const cached = this.cache.get(userId);
            const errorLoadingState = {
                gmb: false,
                facebook: false,
                opinionSystem: false,
            };

            if (cached) {
                this.cache.set(userId, {
                    ...cached,
                    loadingState: errorLoadingState,
                    isInitializing: false,
                });

                if (cached.hasInitialData) {
                    return {
                        reviews: cached.reviews,
                        platformRatings: cached.platformRatings,
                        loadingState: errorLoadingState,
                    };
                }
            }

            throw error;
        }
    }

    async updateReviewStatus(userId: string, reviewsToUpdate: Review[]): Promise<void> {
        const cached = this.cache.get(userId);
        if (!cached) {
            console.warn('No cache found for userId:', userId);
            return;
        }

        const reviewsStatusMap: Record<string, string> = {};
        reviewsToUpdate
            .filter(review => review.status === 'unread' && !review.hasBeenRead)
            .forEach(review => {
                reviewsStatusMap[review.id] = 'read';
            });

        if (Object.keys(reviewsStatusMap).length === 0) {
            return;
        }

        const updatedReviews = cached.reviews.map(review =>
            reviewsStatusMap[review.id]
                ? { ...review, hasBeenRead: true, status: 'read' as const }
                : review,
        );

        this.cache.set(userId, {
            ...cached,
            reviews: updatedReviews,
        });

        invalidateUnreadReviewsCache(userId);

        const newUnreadCount = getCountUnreadReviewsFromData(updatedReviews);
        updateGlobalUnreadCount(userId, newUnreadCount);

        this.scheduleBatchSync(userId, reviewsStatusMap);
    }

    private scheduleBatchSync(userId: string, reviewsStatusMap: Record<string, string>): void {
        const pending = this.pendingUpdates.get(userId) || [];

        Object.entries(reviewsStatusMap).forEach(([reviewId, newStatus]) => {
            const existingIndex = pending.findIndex(p => p.reviewId === reviewId);
            if (existingIndex >= 0) {
                pending[existingIndex] = { reviewId, newStatus, timestamp: Date.now() };
            } else {
                pending.push({ reviewId, newStatus, timestamp: Date.now() });
            }
        });

        this.pendingUpdates.set(userId, pending);

        setTimeout(() => this.syncPendingUpdates(userId), 1000);
    }

    private async syncPendingUpdates(userId: string): Promise<void> {
        if (this.syncInProgress.has(userId)) {
            return;
        }

        const pending = this.pendingUpdates.get(userId);
        if (!pending || pending.length === 0) {
            return;
        }

        this.syncInProgress.add(userId);

        try {
            const reviewsStatusMap: Record<string, string> = {};
            pending.forEach(update => {
                reviewsStatusMap[update.reviewId] = update.newStatus;
            });

            await updateReviewsStatus(userId, reviewsStatusMap);

            this.pendingUpdates.delete(userId);
        } catch (error) {
            console.error('Error during backend synchronization:', error);

            setTimeout(() => {
                this.syncInProgress.delete(userId);
                this.syncPendingUpdates(userId);
            }, 5000);
        } finally {
            this.syncInProgress.delete(userId);
        }
    }

    getCacheInfo(userId: string): CachedData | undefined {
        return this.cache.get(userId);
    }

    async refreshCacheAfterCommentsCheck(userId: string): Promise<void> {
        const cached = this.cache.get(userId);
        if (!cached || !cached.hasInitialData) {
            return;
        }

        try {
            const commentsData = await getReviewsComments(userId);

            if (commentsData) {
                const reviewsWithComments = this.mergeCommentsWithReviews(
                    cached.reviews,
                    commentsData,
                    userId,
                );

                this.cache.set(userId, {
                    ...cached,
                    reviews: reviewsWithComments,
                    lastFetch: Date.now(),
                });

                this.notifySubscribers(userId);
            }
        } catch (error) {
            console.warn(
                `[refreshCacheAfterCommentsCheck] ⚠️ Erreur lors du rafraîchissement pour ${userId}:`,
                error,
            );
        }
    }

    subscribe(userId: string, callback: () => void): () => void {
        if (!this.subscribers.has(userId)) {
            this.subscribers.set(userId, new Set());
        }
        this.subscribers.get(userId)!.add(callback);

        return () => {
            const userSubscribers = this.subscribers.get(userId);
            if (userSubscribers) {
                userSubscribers.delete(callback);
                if (userSubscribers.size === 0) {
                    this.subscribers.delete(userId);
                }
            }
        };
    }

    private preloadFacebookCommentsInBackground(userId: string): void {
        if (this.preloadingComments.has(userId)) {
            return;
        }

        const cachedData = this.cache.get(userId);
        const hasFacebookReviews =
            cachedData?.reviews.some(review => review.network === 'facebook') || false;

        if (!hasFacebookReviews) {
            return;
        }

        this.preloadingComments.add(userId);

        getReviewsComments(userId)
            .then(commentsData => {
                const cachedData = this.cache.get(userId);
                if (cachedData && commentsData) {
                    const reviewsWithComments = this.mergeCommentsWithReviews(
                        cachedData.reviews,
                        commentsData,
                        userId,
                    );

                    this.cache.set(userId, {
                        ...cachedData,
                        reviews: reviewsWithComments,
                        lastFetch: Date.now(),
                    });

                    this.notifySubscribers(userId);
                }
            })
            .catch(error => {
                console.warn(
                    `[preloadFacebookCommentsInBackground] ⚠️ Erreur lors du préchargement pour ${userId}:`,
                    error,
                );
            })
            .finally(() => {
                this.preloadingComments.delete(userId);
            });
    }

    private notifySubscribers(userId: string): void {
        const userSubscribers = this.subscribers.get(userId);
        if (userSubscribers) {
            userSubscribers.forEach(callback => callback());
        }
    }

    async getReviewsWithCommentsOptimized(
        userId: string,
        onComplete?: (reviews: Review[], platformRatings: PlatformRating[]) => void,
    ): Promise<{
        reviews: Review[];
        platformRatings: PlatformRating[];
    }> {
        if (this.commentsPromises.has(userId)) {
            try {
                const result = await this.commentsPromises.get(userId);
                onComplete?.(result.reviews, result.platformRatings);
                return result;
            } catch (error) {
                console.warn(
                    `[getReviewsWithCommentsOptimized] Erreur lors de l'attente de la requête en cours:`,
                    error,
                );
            }
        }

        const commentsPromise = this.executeGetReviewsWithCommentsOptimized(userId, onComplete);
        this.commentsPromises.set(userId, commentsPromise);

        try {
            const result = await commentsPromise;
            this.commentsPromises.delete(userId);
            return result;
        } catch (error) {
            this.commentsPromises.delete(userId);
            throw error;
        }
    }

    private async executeGetReviewsWithCommentsOptimized(
        userId: string,
        onComplete?: (reviews: Review[], platformRatings: PlatformRating[]) => void,
    ): Promise<{
        reviews: Review[];
        platformRatings: PlatformRating[];
    }> {
        try {
            let cachedData = this.cache.get(userId);

            if (!cachedData || !cachedData.hasInitialData) {
                await this.getReviews(userId, false);
                cachedData = this.cache.get(userId);
            }

            if (!cachedData || cachedData.reviews.length === 0) {
                return {
                    reviews: [],
                    platformRatings: [],
                };
            }

            const baseReviews = [...cachedData.reviews];
            const basePlatformRatings = [...cachedData.platformRatings];

            const hasPreloadedComments = baseReviews.some(
                review =>
                    review.review_reply &&
                    (typeof review.review_reply === 'object'
                        ? review.review_reply.comment
                        : review.review_reply),
            );

            if (hasPreloadedComments) {
                const result = {
                    reviews: baseReviews,
                    platformRatings: basePlatformRatings,
                };
                onComplete?.(baseReviews, basePlatformRatings);
                return result;
            }

            try {
                const commentsData = await getReviewsComments(userId);
                const reviewsWithComments = this.mergeCommentsWithReviews(
                    baseReviews,
                    commentsData,
                    userId,
                );

                this.cache.set(userId, {
                    ...cachedData,
                    reviews: reviewsWithComments,
                    lastFetch: Date.now(),
                });

                const result = {
                    reviews: reviewsWithComments,
                    platformRatings: basePlatformRatings,
                };

                onComplete?.(reviewsWithComments, basePlatformRatings);
                return result;
            } catch (commentsError) {
                console.warn(
                    `[getReviewsWithCommentsOptimized] Error loading comments, using data without comments:`,
                    commentsError,
                );

                const result = {
                    reviews: baseReviews,
                    platformRatings: basePlatformRatings,
                };

                onComplete?.(baseReviews, basePlatformRatings);
                return result;
            }
        } catch (error) {
            console.error('[getReviewsWithCommentsOptimized] Erreur:', error);
            throw error;
        }
    }

    private mergeCommentsWithReviews(
        reviews: Review[],
        commentsData: any,
        userId: string,
    ): Review[] {
        if (!commentsData || !commentsData.reviews) {
            return reviews;
        }

        const commentsMap = new Map();
        commentsData.reviews.forEach((reviewWithComments: any) => {
            if (reviewWithComments.id && reviewWithComments.comments) {
                commentsMap.set(reviewWithComments.id, reviewWithComments.comments);
            }
        });

        let statusUpdated = false;

        const updatedReviews = reviews.map(review => {
            const reviewComments = commentsMap.get(review.id);
            let updatedReview = { ...review };

            if (reviewComments && reviewComments.length > 0) {
                let hasReply = false;

                if (review.network === 'facebook') {
                    const pageReply = reviewComments.find(
                        (comment: any) => comment.is_page_reply === true,
                    );
                    if (pageReply) {
                        updatedReview.review_reply = {
                            comment: pageReply.message,
                            updateTime: pageReply.created_time,
                        };
                        hasReply = true;
                    }
                } else if (
                    review.network === 'google_my_business' ||
                    review.network === 'opinion_system'
                ) {
                    const reply = reviewComments[0];

                    if (reply) {
                        updatedReview.review_reply = typeof reply === 'object' ? reply : reply;
                        hasReply = true;
                    }
                }

                if (hasReply && updatedReview.status !== 'answered') {
                    updatedReview.status = 'answered';
                    statusUpdated = true;
                }

                updatedReview.comments = reviewComments;
            }

            return updatedReview;
        });

        if (statusUpdated) {
            const newUnreadCount = getCountUnreadReviewsFromData(updatedReviews);

            updateGlobalUnreadCount(userId, newUnreadCount);

            setTimeout(() => {
                this.notifySubscribers(userId);
            }, 100);
        }

        return updatedReviews;
    }
}

export const reviewsCacheService = new ReviewsCacheService();
